import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  Divider,
  Text,
  ToggleButton,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { ProjectDetailsPageSettingsPageBudgetSectionFragment } from 'generated/resources/graphql'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import LabeledInput from 'modules/core/components/LabeledInput'
import MoneyField from 'modules/core/components/MoneyField'
import NumberField from 'modules/core/components/NumberField'
import { FormErrors } from 'modules/core/hooks/useForm'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { DEFAULT_CURRENCY } from 'modules/project_management/lib/constants'
import { budgetTypesEnum } from 'modules/project_management/lib/constants'

export interface IProjectBudgetValues {
  budgetType: budgetTypesEnum
  feeAmountCents: number | null | undefined
  fixedCostCents: number | null | undefined
  currencyType: string
  plannedCents: number | null | undefined
  plannedMinutes: number | null | undefined
  isBillable: boolean
  delegated: boolean
}

interface IProjectBudgetFormProps {
  projectId: string
  originalBudgetStrategy?: ProjectDetailsPageSettingsPageBudgetSectionFragment['budgetStrategy']
  onChangeValue: (
    fieldName: string,
    fieldValue: number | string | boolean | undefined,
    shouldValidate?: boolean
  ) => void
  values: IProjectBudgetValues
  isOwner: boolean
  errors: FormErrors<IProjectBudgetValues>
  hasBudgetFeature: boolean
  hasCostsFeature: boolean
  strategyType: budgetTypesEnum
  fixedCostCents: number | null | undefined
  shouldShowSaveButton: boolean
  isSubmitting: boolean
  onSubmit: () => void
}

export const ProjectBudgetStrategiesForm = ({
  projectId,
  originalBudgetStrategy,
  onChangeValue,
  values,
  strategyType,
  shouldShowSaveButton,
  isSubmitting,
  onSubmit,
}: IProjectBudgetFormProps) => {
  const [selectedValue, setSelectedValue] = useState<string>(
    values.delegated ? 'phases' : 'project'
  )
  const handleDelegationChange = (delegateToPhases: boolean) => {
    setSelectedValue(delegateToPhases ? 'phases' : 'project')
    onChangeValue('delegated', delegateToPhases)
  }

  const showAlertPhases = useMemo(() => {
    return (
      originalBudgetStrategy?.budgetType === values.budgetType &&
      values.delegated === originalBudgetStrategy?.delegated &&
      values.delegated
    )
  }, [
    originalBudgetStrategy?.budgetType,
    originalBudgetStrategy?.delegated,
    values.budgetType,
    values.delegated,
  ])

  const estimatedCostLabel =
    strategyType === budgetTypesEnum.totalBudget
      ? i18n.t(
          'project_management.settings.sections.budget_strategies_section.common.estimated_cost_title'
        )
      : i18n.t(
          'project_management.settings.sections.budget_strategies_section.common.estimated_total_cost_title'
        )

  const estimatedBudgetTitle =
    strategyType === budgetTypesEnum.totalBudget
      ? i18n.t(
          'project_management.settings.sections.budget_strategies_section.common.estimated_budget_title'
        )
      : i18n.t('finance.account_ledger.new_modal.types.income')

  const estimatedBudgetDescription =
    strategyType === budgetTypesEnum.totalBudget
      ? i18n.t(
          'project_management.settings.sections.budget_strategies_section.common.estimated_budget_description'
        )
      : i18n.t('project_management.modals.create_project.wizard.step_three.income_alert')

  const estimatedBudgetSubtitle =
    strategyType === budgetTypesEnum.totalBudget
      ? i18n.t(
          'project_management.forms.project_planning.project_budget.labels.total_budget.description'
        )
      : undefined

  const handleSubmitClick = () => {
    onSubmit()
  }

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const handleNavigateToSubprojects = () => {
    navigate(navigation.projects.id.details.subprojects.fullPath({ id: projectId }))
  }

  const shouldRenderSaveButton = shouldShowSaveButton || isSubmitting

  return (
    <Box gap="s24">
      <Box gap="s8" flexDirection="column" justifyContent="spaceBetween">
        <Box gap="s8" flexDirection="row">
          <Text.Normal weight="semibold" size="200">
            {i18n.t(
              'project_management.settings.sections.budget_strategies_section.common.budget_type_title'
            )}
          </Text.Normal>
          <InformationTooltip
            information={i18n.t(
              'project_management.settings.sections.budget_strategies_section.common.budget_type_description'
            )}
          />
        </Box>
        <Box gap="s8" flexDirection="row">
          <ToggleButton
            toggled={selectedValue === 'project'}
            onClick={() => handleDelegationChange(false)}
            label={i18n.t('finance.sale_invoices.form.project')}
          />
          <ToggleButton
            toggled={selectedValue === 'phases'}
            onClick={() => handleDelegationChange(true)}
            label={i18n.t('finance.quotes.quote_phases_table.title')}
          />
        </Box>
      </Box>
      {selectedValue === 'project' ? (
        <>
          <Divider />
          <Box gap="s24" flexDirection="column" justifyContent="spaceBetween">
            {values.budgetType !== budgetTypesEnum.withoutBudget && (
              <LabeledInput
                description={i18n.t(
                  'project_management.settings.sections.budget_strategies_section.common.estimated_cost_subtitle'
                )}
                label={estimatedCostLabel}
                secondaryLabel={
                  <InformationTooltip
                    information={i18n.t(
                      'project_management.forms.project_planning.project_budget.labels.estimated_cost_hint'
                    )}
                  />
                }
              >
                <MoneyField
                  currency={values.currencyType || DEFAULT_CURRENCY}
                  placeholder="0"
                  onChange={(value) => onChangeValue('plannedCents', value)}
                  value={values.plannedCents?.toString() || ''}
                  error={''}
                  maxDecimals={2}
                />
              </LabeledInput>
            )}
            <LabeledInput
              label={i18n.t('project_management.imports.table.headers.project_fixed_cost')}
              secondaryLabel={
                <InformationTooltip
                  information={i18n.t(
                    'project_management.forms.financial_details.fixed_costs.information'
                  )}
                />
              }
            >
              <MoneyField
                currency={values.currencyType || DEFAULT_CURRENCY}
                placeholder="0"
                value={values.fixedCostCents?.toString() || ''}
                onChange={(value) => onChangeValue('fixedCostCents', value)}
                error={''}
                maxDecimals={2}
              />
            </LabeledInput>
          </Box>
          {values.budgetType === budgetTypesEnum.totalBudget && (
            <>
              <Divider />
              <NumberField
                label={i18n.t(
                  'project_management.settings.sections.budget_strategies_section.common.estimated_hours_title'
                )}
                value={values.plannedMinutes || null}
                onChange={(value) => {
                  onChangeValue('plannedMinutes', value?.toString() || '')
                }}
                error={''}
                maxDecimals={2}
                placeholder="0"
              />
            </>
          )}
          <Divider />
          <Box gap="s24" flexDirection="column" justifyContent="spaceBetween">
            <Box gap="s8" flexDirection="row" justifyContent="spaceBetween">
              <Text.Normal weight="semibold" size="200">
                {i18n.t('project_management.forms.financial_details.billableToggle.label')}
              </Text.Normal>
              <TogglerCheckbox
                value={values.isBillable}
                onChange={(value) => onChangeValue('isBillable', value)}
                labelBefore
              />
            </Box>
            {values.isBillable && values.budgetType !== budgetTypesEnum.withoutBudget && (
              <Box gap="s8" flexDirection="row">
                <LabeledInput
                  label={estimatedBudgetTitle}
                  secondaryLabel={<InformationTooltip information={estimatedBudgetDescription} />}
                  description={estimatedBudgetSubtitle}
                >
                  <MoneyField
                    currency={values.currencyType || DEFAULT_CURRENCY}
                    placeholder="0"
                    value={values.feeAmountCents?.toString() || ''}
                    onChange={(value) => onChangeValue('feeAmountCents', value)}
                    error={''}
                    maxDecimals={2}
                  />
                </LabeledInput>
              </Box>
            )}
            {shouldRenderSaveButton && (
              <Box flexDirection="row" justifyContent="flexEnd">
                <Button
                  type="primary"
                  onClick={() => handleSubmitClick()}
                  iconName="save"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? i18n.t('common.saving') : i18n.t('common.save_changes')}
                </Button>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <>
          <Divider />
          <F0Alert
            description={i18n.t(
              'project_management.settings.sections.budget_strategies_section.one_alert.description'
            )}
            title={i18n.t(
              'project_management.settings.sections.budget_strategies_section.one_alert.title'
            )}
            variant="info"
          />
          {shouldRenderSaveButton && !showAlertPhases && (
            <Box flexDirection="row" justifyContent="flexEnd">
              <Button
                type="primary"
                onClick={() => handleSubmitClick()}
                iconName="save"
                disabled={isSubmitting}
              >
                {isSubmitting ? i18n.t('common.saving') : i18n.t('common.save_changes')}
              </Button>
            </Box>
          )}
          {showAlertPhases && (
            <Box flexDirection="row" justifyContent="flexEnd">
              <Button
                type="secondary"
                onClick={handleNavigateToSubprojects}
                iconName="external link"
              >
                {i18n.t(
                  'project_management.settings.sections.budget_strategies_section.one_alert.label'
                )}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}
